<template>
  <div>
    <Transition v-bind="fadeInTransition">
      <div v-show="!editing" class="flex flex-col justify-evenly flex-wrap">
        <div class="flex flex-row w-full justify-evenly">
          <Card class="h-fit w-full" pt:content:class="p-0!" pt:body:class="p-2!">
            <template #content>
              <div class="flex flex-row justify-evenly flex-wrap gap-2">
                <div class="flex flex-row gap-8 justify-evenly">
                  <div class="flex flex-col ml-auto my-auto">
                    <small class="my-auto">Draw Name</small>
                    <InputText v-model="project.projectDrawInfo.name" class="h-8 w-32" maxlength="10"> </InputText>
                  </div>
                  <div class="flex flex-col my-auto">
                    <small class="my-auto">Inspection Date</small>
                    <DatePicker
                      v-model="project.projectDrawInfo.inspectionDate"
                      disabled
                      iconDisplay="input"
                      inputId="inspectionDate"
                      class="h-8"
                      inputClass="w-32"
                      panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                      dateFormat="m/d/yy"
                    />
                  </div>
                  <div class="flex flex-col ml-auto my-auto">
                    <small class="my-auto">Date Created</small>
                    <DatePicker
                      v-model="project.projectDrawInfo.dateCreated"
                      showIcon
                      :show-on-focus="false"
                      icon-display="button"
                      inputId="dateCreated"
                      class="h-8"
                      inputClass="w-28 pr-1!"
                      panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                      dateFormat="m/d/yy"
                    />
                  </div>
                  <div class="flex flex-col my-auto">
                    <small class="my-auto">Completion Date</small>
                    <DatePicker
                      v-model="project.projectDrawInfo.completionDate"
                      show-button-bar
                      showIcon
                      :show-on-focus="false"
                      icon-display="button"
                      inputId="completionDate"
                      class="h-8"
                      inputClass="w-28 pr-1!"
                      panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                      dateFormat="m/d/yy"
                    />
                  </div>
                </div>
                <div class="flex flex-col h-full w-fit justify-between my-auto gap-2">
                  <div class="flex flex-row my-auto">
                    <Checkbox v-model="project.projectDrawInfo.onSchedule" :binary="true"></Checkbox>
                    <p class="my-auto mx-2">On Schedule</p>
                  </div>
                  <div class="flex flex-row my-auto">
                    <Checkbox v-model="project.projectDrawInfo.dateDown" :binary="true"></Checkbox>
                    <p class="my-auto mx-2">Date Down</p>
                    <p :class="'my-auto ' + dateDownColorClass()">{{ dateDownLabel }}</p>
                  </div>
                </div>
                <SubmitButton class="h-10 my-auto" @click="infoUpdated"></SubmitButton>
                <Divider layout="vertical" />
                <div class="h-10 my-auto">
                  <Button
                    label="Draw Details"
                    type="button"
                    icon-pos="right"
                    icon="pi pi-ellipsis-v"
                    @click="actionMenu?.toggle"
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                    class="h-10"
                    outlined
                  ></Button>
                </div>
                <Menu ref="actionMenu" :model="drawActionItems" popup></Menu>
              </div>
            </template>
          </Card>
        </div>
        <div class="flex flex-row flex-wrap w-full justify-evenly my-2 gap-2">
          <Card class="px-5 my-auto w-auto h-[22.5rem]" pt:body:class="h-full p-0!" pt:content:class="p-0! h-full">
            <template #content>
              <div class="flex flex-col h-full justify-evenly">
                <div class="flex flex-row gap-2 justify-center">
                  <div class="flex flex-col">
                    <Knob v-model="percentDrawnTween" :valueTemplate="project.projectDrawInfo.percentDrawn + '%'" readonly :size="140" />
                    <p class="text-center font-bold -mt-3">Budget Drawn</p>
                  </div>
                  <p
                    class="text-center font-bold my-auto text-xl w-10"
                    :class="
                      prevProjectHeader == null || project.projectDrawInfo.percentDrawn - prevProjectHeader.percentDrawn >= 0
                        ? 'text-success-500'
                        : 'text-danger-500'
                    "
                  >
                    <span
                      class="font-bold"
                      :class="
                        prevProjectHeader == null || project.projectDrawInfo.percentDrawn - prevProjectHeader.percentDrawn >= 0
                          ? 'pi pi-arrow-circle-up'
                          : 'pi pi-arrow-circle-down'
                      "
                    ></span>
                    {{ budgetDrawnPercentChange }}%
                  </p>
                </div>
                <div class="flex flex-row gap-2 justify-center">
                  <div class="flex flex-col">
                    <Knob v-model="percentCompleteTween" :valueTemplate="project.projectDrawInfo.percentComplete + '%'" readonly :size="140" />
                    <p class="text-center font-bold -mt-3">Site Complete</p>
                  </div>
                  <p
                    class="text-center font-bold my-auto text-xl w-10"
                    :class="
                      prevProjectHeader == null || project.projectDrawInfo.percentComplete - prevProjectHeader.percentComplete >= 0
                        ? 'text-success-500'
                        : 'text-danger-500'
                    "
                  >
                    <span
                      class="font-bold"
                      :class="
                        prevProjectHeader == null || project.projectDrawInfo.percentComplete - prevProjectHeader.percentComplete >= 0
                          ? 'pi pi-arrow-circle-up'
                          : 'pi pi-arrow-circle-down'
                      "
                    ></span>
                    {{ siteCompletePercentChange }}%
                  </p>
                </div>
                <div class="flex flex-row gap-2">
                  <LabeledCheckboxNoForm
                    v-model="project.projectDrawInfo.totalSiteCompleteOverride"
                    name="totalSiteCompleteOverride"
                    label="Override"
                    label-location="right"
                    class="my-auto"
                    size="w-fit"
                    :disabled="overrideUpdating || !canEdit"
                  ></LabeledCheckboxNoForm>
                  <InputNumber
                    v-model="project.projectDrawInfo.percentComplete"
                    class="h-8"
                    inputClass="w-14"
                    :min="0"
                    :allowEmpty="false"
                    :maxFractionDigits="0"
                    :disabled="!project.projectDrawInfo.totalSiteCompleteOverride || overrideUpdating || !canEdit"
                  ></InputNumber>
                  <IconButton v-tooltip.top="'Save'" icon="save" small :disabled="overrideUpdating || !canEdit" @click="updateSiteCompleted">
                  </IconButton>
                </div>
              </div>
            </template>
          </Card>
          <div class="flex flex-col my-auto gap-3 w-fit min-w-[500px] flex-none">
            <Card class="px-1 h-fit w-full my-auto" pt:content:class="p-0!">
              <template #content>
                <p>All Funds</p>
                <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(displayValues.funds) }}</h1>
                <div class="flex flex-row gap-8 justify-evenly">
                  <div class="flex flex-col">
                    <small>Disbursed</small>
                    <p class="font-bold text-xl">{{ formatCurrency(displayValues.fundsDisbursed) }}</p>
                  </div>
                  <div class="flex flex-col">
                    <small>Retained</small>
                    <p class="font-bold text-xl">{{ formatCurrency(displayValues.fundsRetained) }}</p>
                  </div>
                  <div class="flex flex-col">
                    <small>Available</small>
                    <p class="font-bold text-xl">{{ formatCurrency(displayValues.fundsAvailable) }}</p>
                  </div>
                </div>
              </template>
            </Card>
            <Card class="px-1 h-fit w-full my-auto" pt:content:class="p-0!">
              <template #content>
                <p>Budget Amount</p>
                <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(displayValues.budget) }}</h1>
                <div class="flex flex-row gap-8 justify-evenly">
                  <div class="flex flex-col">
                    <small>Total Drawn</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.drawn)">
                      {{ formatCurrency(displayValues.drawn) }}
                    </p>
                  </div>
                  <div class="flex flex-col">
                    <small>Total Variance</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.variance, true)">
                      {{ formatCurrency(displayValues.variance) }}
                    </p>
                  </div>
                  <div class="flex flex-col">
                    <small>Required</small>
                    <p
                      :class="
                        'font-bold text-xl ' +
                        currencyColorClass(project.projectDrawInfo.fundsAvailable - project.projectDrawInfo.fundsRequired, false)
                      "
                    >
                      {{ formatCurrency(displayValues.fundsRequired) }}
                    </p>
                  </div>
                </div>
              </template>
            </Card>
          </div>
          <div class="flex flex-col w-fit gap-2 flex-none">
            <Card class="px-5 my-auto h-full w-full" pt:body:class="h-full px-3 p-5" pt:content:class="p-0! h-full">
              <template #content>
                <div class="flex flex-col h-full justify-between">
                  <div class="flex flex-col">
                    <small>Fund v Budget</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.fundsVsBudget)">
                      {{ formatCurrency(displayValues.fundsVsBudget) }}
                    </p>
                  </div>
                  <div class="flex flex-col">
                    <small>Actual Variance</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.actualVariance, true)">
                      {{ formatCurrency(displayValues.actualVariance) }}
                    </p>
                  </div>
                  <div class="flex flex-col">
                    <small>Estimated Variance</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.estimatedVariance, true)">
                      {{ formatCurrency(displayValues.estimatedVariance) }}
                    </p>
                  </div>
                  <div class="flex flex-col">
                    <small>Difference</small>
                    <p :class="'font-bold text-xl ' + currencyColorClass(project.projectDrawInfo.difference)">
                      {{ formatCurrency(displayValues.difference) }}
                    </p>
                  </div>
                </div>
              </template>
            </Card>
          </div>
          <Card class="px-2 h-auto grow">
            <template #content>
              <div class="flex flex-col w-full mx-auto">
                <div class="flex flex-row gap-2">
                  <p class="font-bold flex-none w-40"></p>
                  <p class="font-bold flex-none w-36">Balance</p>
                  <p class="font-bold flex-none w-36">Available</p>
                </div>
                <Divider />
                <div class="flex flex-row gap-2">
                  <p class="font-bold flex-none w-40 my-auto">Total Funds</p>
                  <p class="font-bold flex-none w-36 my-auto">
                    {{ formatCurrency(displayValues.principalBalance + displayValues.equityBalance) }}
                  </p>
                  <p class="font-bold flex-none w-36 my-auto">
                    {{ formatCurrency(displayValues.committedAvailable + displayValues.equityAvailable) }}
                  </p>
                  <div class="w-full min-w-24">
                    <MeterGroup :value="totalInfo">
                      <template #start="{ totalPercent }">
                        <div class="flex justify-content-between h-6 -mb-5 relative font-bold">
                          <span class="absolute text-right transition-all ease-out duration-500">{{ totalPercent }}%</span>
                        </div>
                      </template>
                      <template #label><div></div></template>
                    </MeterGroup>
                  </div>
                </div>
                <Divider />
                <div class="flex flex-row gap-2">
                  <p class="font-bold flex-none w-40 my-auto">Principal Funds</p>
                  <p class="font-bold flex-none w-36 my-auto">{{ formatCurrency(displayValues.principalBalance) }}</p>
                  <p class="font-bold flex-none w-36 my-auto">{{ formatCurrency(displayValues.committedAvailable) }}</p>
                  <div class="w-full min-w-24">
                    <MeterGroup :value="principalInfo">
                      <template #start="{ totalPercent }">
                        <div class="flex justify-content-between h-6 -mb-5 relative font-bold">
                          <span class="absolute text-right transition-all ease-out duration-500">{{ totalPercent }}%</span>
                        </div>
                      </template>
                      <template #label><div></div></template>
                    </MeterGroup>
                  </div>
                </div>
                <Divider />
                <div class="flex flex-row gap-2">
                  <p class="font-bold flex-none w-40 my-auto">Equity Funds</p>
                  <p class="font-bold flex-none w-36 my-auto">{{ formatCurrency(displayValues.equityBalance) }}</p>
                  <p class="font-bold flex-none w-36 my-auto">{{ formatCurrency(displayValues.equityAvailable) }}</p>
                  <div class="w-full min-w-24">
                    <MeterGroup :value="equityInfo">
                      <template #start="{ totalPercent }">
                        <div class="flex justify-content-between h-6 -mb-5 relative font-bold">
                          <span class="absolute text-right transition-all ease-out duration-500">{{ totalPercent }}%</span>
                        </div>
                      </template>
                      <template #label><div></div></template>
                    </MeterGroup>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </Transition>
    <Transition v-bind="fadeInTransition">
      <SourceOfFundsInformation
        v-show="!editing || (editing && display === 'Sources')"
        v-model:project="project"
        v-model:draw-items="drawItems"
        v-model:source-of-funds="sourceOfFunds"
        v-model:editing="editing"
        v-model:editing-title="editingTitle"
        :isCurrentDraw
        :permissions
        :can-edit
        :entities
        :contacts
        @sources-updated="$emit('dataUpdated')"
        @start-editing="display = 'Sources'"
      ></SourceOfFundsInformation>
    </Transition>

    <Transition v-bind="pushUpTransition">
      <Card v-if="editing && display === 'Retainage'" class="h-fit w-full" pt:content:class="p-0!">
        <template #content>
          <MaintainRetainage
            v-model:project="project"
            v-model:source-of-funds="sourceOfFunds"
            v-model:entity-retainage-list="entityRetainageList"
            :get-retainage="true"
            :can-edit
            @submitted="submitRetainage"
            @cancelled="retainageClosed"
          ></MaintainRetainage>
        </template>
      </Card>
    </Transition>

    <Transition v-bind="pushUpTransition">
      <Card v-if="editing && display === 'Waivers'" class="h-fit w-full" pt:content:class="p-0!">
        <template #content>
          <MaintainWaivers
            :project="project"
            :permissions="permissions"
            :can-edit
            @cancelled="waiversClosed"
            @submitted="waiversClosed"
          ></MaintainWaivers>
        </template>
      </Card>
    </Transition>

    <TSDialog v-model:visible="showNotes" :label="(canEdit ? 'Edit' : 'Display') + ' Draw Notes'" icon="pencil" size="h-fit w-[1000px]!">
      <template #content>
        <Textarea
          v-model="drawNotes"
          auto-resize
          :placeholder="canEdit ? 'Draw Notes' : ''"
          :disabled="!canEdit"
          rows="10"
          class="w-full my-4"
        ></Textarea>
        <div class="flex justify-end">
          <SubmitButton @click="submitDrawNotes"></SubmitButton>
        </div>
      </template>
    </TSDialog>
    <TSDialog v-model:visible="showFiles" label="Draw Files" icon="file" size="h-fit w-[1000px]!">
      <template #content><FileManager :file-entity-type="FileEntity.DRAW" :files-url="drawFilesUrl" upload-mode="advanced" /></template>
    </TSDialog>
    <TSDialog
      v-model:visible="showInspections"
      :label="(canEdit ? 'Edit' : 'Display') + ' Inspection Details'"
      icon="clipboard"
      size="h-fit w-[1000px]!"
    >
      <template #content><InspectionsTab :project :can-edit @inspection-updated="inspectionsUpdated" /></template>
    </TSDialog>
    <TSDialog v-model:visible="showDrawBasedRetainage" label="Draw Based Retainage" icon="percentage" size="h-fit w-[900px]!">
      <template #content>
        <MaintainDrawBasedRetainage
          v-model:retainage="drawBasedRetainage"
          :project
          :can-edit
          :get-retainage="true"
          @cancelled="drawBasedRetainageCancelled"
          @submitted="drawBasedRetainageSubmitted"
        ></MaintainDrawBasedRetainage>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { formatCurrency } from "@/services/Utility";
import type { Project, ProjectDrawInfo } from "@/types/Projects";
import type { MeterItem } from "primevue/metergroup";
import { z } from "zod";
import type { DrawItem } from "@/types/DrawItem";
import type { ProjectEntity } from "@/types/ProjectEntity";
import type { UserPermissions } from "@/types/User";
import gsap from "gsap";
import FileManager from "../FileManager.vue";
import { FileEntity } from "@/enums/FileEntity";
import type Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { useConfirm } from "primevue/useconfirm";
import { checkBudgetBasedReminders } from "@/services/ReminderService";
import type { ProjectReminder } from "@/types/Reminder";
import { fadeInTransition, pushUpTransition } from "@/services/Transitions";
import type { Entity } from "@/types/Entity";
import { type DrawBasedRetainage, DrawBasedRetainageSchema, type EntityRetainage } from "@/types/Retainage";

const project = defineModel<Project>("project", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const sourceOfFunds = defineModel<ProjectEntity[]>("sourceOfFunds", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });
const projectReminders = defineModel<ProjectReminder[]>("projectReminders", { required: true });

const overrideUpdating = ref(false);

const entityRetainageList = ref<EntityRetainage[]>([]);

const drawBasedRetainage = ref<DrawBasedRetainage>(DrawBasedRetainageSchema.parse({}));

const confirm = useConfirm();

const emit = defineEmits<{
  dataUpdated: [];
  updateTimelineName: [];
}>();

interface Props {
  prevProjectHeader: ProjectDrawInfo | null;
  permissions: UserPermissions;
  canEdit: boolean;
  isCurrentDraw: boolean;
  entities: Entity[];
  contacts: ProjectEntity[];
}
const props = defineProps<Props>();

let drawItemHistory = useManualRefHistory(drawItems, { clone: true });
let sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
const retainageSubmitted = ref(false);

const actionMenu = ref();
const percentDrawnTween = ref(0);
const percentCompleteTween = ref(0);
const budgetDrawnPercentChange = ref(0);
const siteCompletePercentChange = ref(0);
const displayValues = reactive({
  funds: 0,
  fundsDisbursed: 0,
  fundsRetained: 0,
  fundsAvailable: 0,
  budget: 0,
  drawn: 0,
  variance: 0,
  fundsRequired: 0,
  fundsVsBudget: 0,
  actualVariance: 0,
  estimatedVariance: 0,
  difference: 0,
  principalBalance: 0,
  committedAvailable: 0,
  equityBalance: 0,
  equityAvailable: 0,
});

const updateAnimations = () => {
  // Animate Percentages
  gsap.to(percentDrawnTween, {
    duration: 0.75,
    value: project.value.projectDrawInfo.percentDrawn > 100 ? 100 : project.value.projectDrawInfo.percentDrawn,
    ease: "power1.out",
  });
  gsap.to(percentCompleteTween, {
    duration: 0.75,
    value: project.value.projectDrawInfo.percentComplete > 100 ? 100 : project.value.projectDrawInfo.percentComplete,
    ease: "power1.out",
  });
  gsap.to(budgetDrawnPercentChange, {
    duration: 0.75,
    value: props.prevProjectHeader == null ? 0 : Math.abs(project.value.projectDrawInfo.percentDrawn - props.prevProjectHeader.percentDrawn),
    ease: "power1.out",
    snap: { value: 1 },
  });
  gsap.to(siteCompletePercentChange, {
    duration: 0.75,
    value: props.prevProjectHeader == null ? 0 : Math.abs(project.value.projectDrawInfo.percentComplete - props.prevProjectHeader.percentComplete),
    ease: "power1.out",
    snap: { value: 1 },
  });

  // Animate all currency values in one go
  gsap.to(displayValues, {
    duration: 0.75,
    ease: "power1.out",
    funds: project.value.projectDrawInfo.funds || 0,
    fundsDisbursed: project.value.projectDrawInfo.fundsDisbursed || 0,
    fundsRetained: project.value.projectDrawInfo.fundsRetained || 0,
    fundsAvailable: project.value.projectDrawInfo.fundsAvailable || 0,
    budget: project.value.projectDrawInfo.budget || 0,
    drawn: project.value.projectDrawInfo.drawn || 0,
    variance: project.value.projectDrawInfo.variance || 0,
    fundsRequired: project.value.projectDrawInfo.fundsRequired || 0,
    fundsVsBudget: project.value.projectDrawInfo.fundsVsBudget || 0,
    actualVariance: project.value.projectDrawInfo.actualVariance || 0,
    estimatedVariance: project.value.projectDrawInfo.estimatedVariance || 0,
    difference: project.value.projectDrawInfo.difference || 0,
    principalBalance: project.value.projectDrawInfo.principalBalance || 0,
    committedAvailable: project.value.projectDrawInfo.committedAvailable || 0,
    equityBalance: project.value.projectDrawInfo.equityBalance || 0,
    equityAvailable: project.value.projectDrawInfo.equityAvailable || 0,
    snap: { value: 0.01 },
  });
};

watch(
  () => project.value.projectDrawInfo,
  () => {
    recalculateFunds();
    updateAnimations();
  },
  { deep: true }
);

const drawActionItems: MenuItem[] = [
  {
    label: "Files",
    icon: "pi pi-copy",
    command: () => (showFiles.value = true),
  },
  {
    label: "Inspections",
    icon: "pi pi-clipboard",
    command: () => (showInspections.value = true),
  },
  {
    label: "Notes",
    icon: "pi pi-file-edit",
    command: () => showDrawNotes(),
  },
  {
    label: "Retainage",
    icon: "pi pi-percentage",
    command: () => showRetainage(),
  },
  {
    label: "Waivers",
    icon: "pi pi-ticket",
    command: () => showWaivers(),
  },
];

const display = ref<"Sources" | "Retainage" | "Waivers">();

const showNotes = ref(false);
const showFiles = ref<boolean>(false);
const showInspections = ref<boolean>(false);
const showDrawBasedRetainage = ref<boolean>(false);

const drawNotes = ref("");

const dateDownLabel = ref("0 of 0");
const checkedDateDownTotals = ref(0);

const drawFilesUrl = computed<string>(() => {
  return `/draw-files/${project.value.projectInfo.projectId}/${project.value.projectDrawInfo.drawNumber}`;
});

const principalTotal = ref(project.value.projectDrawInfo.principalBalance + project.value.projectDrawInfo.committedAvailable);
const equityTotal = ref(project.value.projectDrawInfo.equityBalance + project.value.projectDrawInfo.equityAvailable);
const total = ref(principalTotal.value + equityTotal.value);

const principalInfo = ref<MeterItem[]>([
  {
    label: "",
    value: 0,
    color: "",
    icon: "",
  },
]);

const equityInfo = ref<MeterItem[]>([
  {
    label: "",
    value: 0,
    color: "",
    icon: "",
  },
]);

const totalInfo = ref<MeterItem[]>([
  {
    label: "",
    value: 0,
    color: "",
    icon: "",
  },
]);

const recalculateFunds = (): void => {
  principalTotal.value = project.value.projectDrawInfo.principalBalance + project.value.projectDrawInfo.committedAvailable;
  const principalValue = principalTotal.value === 0 ? 0 : (project.value.projectDrawInfo.principalBalance / principalTotal.value) * 100;

  equityTotal.value = project.value.projectDrawInfo.equityBalance + project.value.projectDrawInfo.equityAvailable;
  const equityValue = equityTotal.value === 0 ? 0 : (project.value.projectDrawInfo.equityBalance / equityTotal.value) * 100;

  total.value = equityTotal.value + principalTotal.value;
  const totalValue =
    total.value === 0 ? 0 : ((project.value.projectDrawInfo.equityBalance + project.value.projectDrawInfo.principalBalance) / total.value) * 100;

  gsap.to(totalInfo.value[0], {
    duration: 0.75,
    value: totalValue,
  });

  gsap.to(principalInfo.value[0], {
    duration: 0.75,
    value: principalValue,
  });

  gsap.to(equityInfo.value[0], {
    duration: 0.75,
    value: equityValue,
  });
};

const currencyColorClass = (value: number, inverse: boolean = false): string => {
  if (inverse) return value > 0 ? "text-danger-500" : "text-success-500";
  return value < 0 ? "text-danger-500" : "text-success-500";
};

const dateDownColorClass = (): string => {
  return checkedDateDownTotals.value > project.value.projectTotals.dateDowns ? "font-bold text-danger-500" : "text-success-500";
};

const DrawInfoEditSchema = z.object({
  drawId: z.string(),
  dateCreated: z.coerce.date(),
  inspectionDate: z.coerce.date().nullable(),
  completionDate: z.coerce.date().nullable(),
  dateDown: z.boolean(),
  onSchedule: z.boolean(),
  name: z.string(),
});
type DrawInfoEdit = z.infer<typeof DrawInfoEditSchema>;

const showRetainage = (): void => {
  if (drawItems.value.filter((item: DrawItem) => item.description === "DRAW BASED RETAINAGE").length > 0) {
    showDrawBasedRetainage.value = true;
    return;
  }

  drawItemHistory = useManualRefHistory(drawItems, { clone: true });
  sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
  drawItemHistory.commit();
  sourceOfFundsHistory.commit();
  display.value = "Retainage";
  editing.value = true;
  editingTitle.value = "Editing Retainage";
};

const submitRetainage = (): void => {
  drawItemHistory.commit();
  sourceOfFundsHistory.commit();
  retainageSubmitted.value = true;
  emit("dataUpdated");

  retainageClosed();
};

const retainageClosed = (): void => {
  if (!retainageSubmitted.value) {
    drawItemHistory.undo();
    sourceOfFundsHistory.undo();
  }

  retainageSubmitted.value = false;
  editing.value = false;
  editingTitle.value = "";
};

const drawBasedRetainageCancelled = (): void => {
  showDrawBasedRetainage.value = false;
};

const drawBasedRetainageSubmitted = (): void => {
  showDrawBasedRetainage.value = false;
  emit("dataUpdated");
};

const inspectionsUpdated = (): void => {
  emit("dataUpdated");
  showInspections.value = false;
};

const showWaivers = (): void => {
  editing.value = true;
  editingTitle.value = "Editing Waivers";
  display.value = "Waivers";
};

const waiversClosed = (): void => {
  editing.value = false;
  editingTitle.value = "";
};

const showDrawNotes = (): void => {
  drawNotes.value = project.value.projectDrawInfo.drawNotes ? project.value.projectDrawInfo.drawNotes : "";
  showNotes.value = true;
};

const submitDrawNotes = (): void => {
  let data = new FormData();
  data.append("drawNotes", drawNotes.value);
  project.value.projectDrawInfo.drawNotes = drawNotes.value;
  axios
    .post(`/draw/edit/notes/${project.value.projectDrawInfo.drawId}`, data)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          success("Draw notes updated", "");
          showNotes.value = false;
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const updateSiteCompleted = (): void => {
  overrideUpdating.value = true;
  let url = `/draw/${project.value.projectDrawInfo.drawId}/edit/percent-complete`;
  if (project.value.projectDrawInfo.totalSiteCompleteOverride) url += `?override=${project.value.projectDrawInfo.percentComplete}`;
  axios
    .post(url)
    .then((response) =>
      handleApiResponse(
        response,
        (data: number) => {
          project.value.projectDrawInfo.percentComplete = data == null ? 0 : data;

          checkBudgetBasedReminders(
            confirm,
            projectReminders.value,
            drawItems.value,
            project.value.projectDrawInfo.percentComplete,
            project.value.projectDrawInfo.percentDrawn,
            project.value.projectInfo.processor.entityId
          );

          overrideUpdating.value = false;
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
          overrideUpdating.value = false;
        }
      )
    )
    .catch((e) => errorApi(e));
};

const infoUpdated = (): void => {
  const request: DrawInfoEdit = {
    drawId: project.value.projectDrawInfo.drawId,
    dateCreated: project.value.projectDrawInfo.dateCreated,
    inspectionDate: project.value.projectDrawInfo.inspectionDate,
    completionDate: project.value.projectDrawInfo.completionDate,
    dateDown: project.value.projectDrawInfo.dateDown,
    onSchedule: project.value.projectDrawInfo.onSchedule,
    name: project.value.projectDrawInfo.name,
  };

  axios
    .post("/draw/edit-info-field", request)
    .then((response) => {
      getProjectCheckedDateDownTotals();
      emit("updateTimelineName");
      success("Draw Info Updated", "");
    })
    .catch((e) => errorApi(e));
};

const getProjectCheckedDateDownTotals = async (): Promise<void> => {
  await axios
    .get("/projects/checked-date-downs/" + project.value.projectInfo.projectId)
    .then((response) => {
      checkedDateDownTotals.value = response.data.result;
    })
    .catch((e) => errorApi(e));

  dateDownLabel.value = checkedDateDownTotals.value + " of " + project.value.projectTotals.dateDowns;
};

onBeforeMount(async (): Promise<void> => {
  getProjectCheckedDateDownTotals();
  recalculateFunds();
  updateAnimations();
});

//TODO: LIEN WAIVERS
//TODO: Look into date updated????
</script>
